import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useOptimistic, useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InfoCallout } from '@/components/ui/info-callout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useValidateIntegration } from '@/hooks/useValidateIntegration';
import type { OperationsPlugin } from '@/sections/customerManagement/components/masterDashboard/hooks/useAllDiscoveryPlugins';
import {
  useDeleteMasterKey,
  useMasterKey,
  useSetMasterKey,
} from '@/sections/customerManagement/components/masterDashboard/hooks/useMasterKey';
import { useTogglePluginEnabled } from '@/sections/customerManagement/components/masterDashboard/hooks/useTogglePluginEnabled';

interface PluginConfigDialogProps {
  plugin: OperationsPlugin | null;
  onOpenChange: (open: boolean) => void;
}

export function PluginConfigDialog({
  plugin,
  onOpenChange,
}: PluginConfigDialogProps) {
  const { currentTheme } = useTheme();
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());
  const [iconError, setIconError] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [optimisticEnabled, setOptimisticEnabled] = useOptimistic(
    plugin?.enabled ?? true,
    (_current: boolean, newEnabled: boolean) => newEnabled
  );

  const { data: keyInfo } = useMasterKey(
    plugin?.piusId ?? '',
    !!plugin?.piusId
  );
  const { mutateAsync: setMasterKey, isPending: isSaving } = useSetMasterKey();
  const { mutateAsync: deleteMasterKey, isPending: isDeleting } =
    useDeleteMasterKey();
  const { mutateAsync: toggleEnabled, isPending: isToggling } =
    useTogglePluginEnabled();
  const { validateIntegration, isValidating } = useValidateIntegration();

  const isPending = isSaving || isDeleting || isValidating;

  const updateField = (name: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
    setValidationError(null);
  };

  const toggleVisibility = (name: string) => {
    setVisibleFields(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSave = async () => {
    if (!plugin?.piusId) return;
    const apiKeyField = plugin.credentialFields.find(f => f.name === 'api_key');
    const apiKeyValue = fieldValues['api_key']?.trim() ?? '';
    if (apiKeyField && !apiKeyValue && !keyInfo?.configured) return;

    const secretMap: Record<string, string> = {};
    for (const field of plugin.credentialFields) {
      const val = fieldValues[field.name]?.trim();
      if (val !== undefined) secretMap[field.name] = val;
    }

    const { isValid, validationErrors } = await validateIntegration({
      provider: plugin.piusId,
      config: secretMap,
      suppressErrorToast: true,
    });

    if (!isValid) {
      const firstError = validationErrors?.[0];
      setValidationError(
        firstError
          ? `${firstError.title}: ${firstError.message}`
          : 'Validation failed. Please check your credentials.'
      );
      return;
    }

    const extra: Record<string, string> = {};
    for (const field of plugin.credentialFields) {
      if (field.name === 'api_key') continue;
      const val = fieldValues[field.name]?.trim();
      if (val !== undefined) extra[field.name] = val;
    }

    try {
      await setMasterKey({
        plugin: plugin.piusId,
        apiKey: apiKeyValue,
        extra: Object.keys(extra).length > 0 ? extra : undefined,
      });
      handleOpenChange(false);
    } catch {
      // Mutation hook surfaces the error via toast/defaultErrorMessage.
    }
  };

  const handleDelete = async () => {
    if (!plugin?.piusId) return;
    try {
      await deleteMasterKey({ plugin: plugin.piusId });
      setFieldValues({});
    } catch {
      // Mutation hook surfaces the error via toast/defaultErrorMessage.
    }
  };

  const hasRequiredValues = keyInfo?.configured
    ? Object.values(fieldValues).some(v => v.trim().length > 0)
    : (plugin?.credentialFields
        .filter(f => f.required)
        .every(f => (fieldValues[f.name]?.trim() ?? '').length > 0) ?? false);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setFieldValues({});
      setVisibleFields(new Set());
      setValidationError(null);
    }
    setIconError(false);
    onOpenChange(open);
  };

  return (
    <Dialog open={!!plugin} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {plugin && !iconError && (
              <img
                src={`/icons/${currentTheme}/${plugin.id}.svg`}
                alt={plugin.name}
                className="size-5 rounded"
                onError={() => setIconError(true)}
              />
            )}
            Configure {plugin?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Enable / disable toggle */}
          {plugin?.piusId && (
            <div className="flex flex-col gap-1.5">
              <div className="border-chariot-stroke flex items-center justify-between rounded-md border p-3">
                <span className="text-chariot-text-primary text-sm font-medium">
                  Enabled
                </span>
                <Switch
                  checked={optimisticEnabled}
                  onCheckedChange={async (checked: boolean) => {
                    setOptimisticEnabled(checked);
                    try {
                      await toggleEnabled({
                        plugin: plugin.piusId!,
                        enabled: checked,
                      });
                    } catch {
                      // useOptimistic auto-reverts; mutation hook shows error toast
                    }
                  }}
                  disabled={isToggling || plugin.globalEnabled === false}
                  aria-label={`${optimisticEnabled ? 'Disable' : 'Enable'} ${plugin.name}`}
                />
              </div>
              {plugin.globalEnabled === false && (
                <p className="text-chariot-text-tertiary text-xs">
                  All discovery is currently disabled globally.
                </p>
              )}
            </div>
          )}

          {/* Credential fields */}
          {plugin?.credentialFields.map(field => (
            <div key={field.name} className="flex flex-col gap-1.5">
              <Label
                htmlFor={field.name}
                className="text-chariot-text-secondary text-xs font-medium"
              >
                {field.label}
                {field.required && <span className="text-error ml-0.5">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id={field.name}
                  type={
                    field.isPassword && !visibleFields.has(field.name)
                      ? 'password'
                      : 'text'
                  }
                  placeholder={field.placeholder}
                  value={fieldValues[field.name] ?? ''}
                  onChange={e => updateField(field.name, e.target.value)}
                  className={field.isPassword ? 'pr-9' : undefined}
                />
                {field.isPassword && (
                  <button
                    type="button"
                    onClick={() => toggleVisibility(field.name)}
                    className="text-chariot-text-tertiary hover:text-chariot-text-secondary absolute top-1/2 right-3 -translate-y-1/2"
                    aria-label={
                      visibleFields.has(field.name)
                        ? 'Hide field'
                        : 'Show field'
                    }
                  >
                    {visibleFields.has(field.name) ? (
                      <EyeSlashIcon className="size-4" />
                    ) : (
                      <EyeIcon className="size-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Validation error */}
          {validationError && (
            <InfoCallout variant="error" role="alert" aria-live="assertive">
              {validationError}
            </InfoCallout>
          )}

          {/* Current key status */}
          {keyInfo && (
            <div className="flex items-center gap-2">
              <KeyIcon className="text-chariot-text-tertiary size-4 shrink-0" />
              <span className="text-chariot-text-secondary text-xs">
                {keyInfo.configured
                  ? `Current key: ${keyInfo.maskedKey}`
                  : 'No master API key configured'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center">
          {keyInfo?.configured && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={isPending}
              className="text-error hover:text-error mr-auto"
            >
              {isDeleting ? 'Removing...' : 'Clear Key'}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isPending || !hasRequiredValues}
          >
            {isValidating ? 'Validating...' : isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
